import { BarChart, Bar as RBar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Flag } from "lucide-react";
import { TeamLogo } from "./TeamBits";
import { Bar, hotColor } from "./ProbabilityWidget";

const CHART_MARGIN = { top: 4, right: 4, left: -28, bottom: 0 };
const AXIS_TICK = { fill: "#71717A", fontSize: 10 };
const TOOLTIP_STYLE = { background: "#121212", border: "1px solid #27272A", fontSize: 12 };
const TOOLTIP_CURSOR = { fill: "#1A1A1A" };
const f1 = (v) => (v == null ? "–" : Number(v).toFixed(1));

function BigAvg({ value, label, color, testId }) {
  return (
    <div className="border border-[#27272A] p-3" data-testid={testId}>
      <div className="label text-[9px]">{label}</div>
      <div className="heading text-4xl font-black mono tracking-tighter mt-1" style={{ color }}>{f1(value)}</div>
    </div>
  );
}

function TeamCorners({ team, stats, side }) {
  const a = stats.averages;
  const o = stats.over_rates;
  const venueLabel = side === "home" ? "in casa" : "in trasferta";
  const venueFor = side === "home" ? a.corners_for_home : a.corners_for_away;
  const venueAgainst = side === "home" ? a.corners_against_home : a.corners_against_away;
  const chart = [...stats.matches].reverse().map((r, i) => ({
    n: i + 1, label: `${r.venue === "home" ? "vs" : "@"} ${r.opponent}`, Battuti: r.corners_for, Concessi: r.corners_against,
  }));
  return (
    <div className="space-y-5" data-testid={`corner-team-${side}`}>
      <div className="flex items-center gap-3">
        <TeamLogo team={team} size={28} />
        <div className="heading text-xl font-black truncate">{team.name}</div>
        <span className="label text-[9px] ml-auto">{stats.sample} gare</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <BigAvg value={a.corners_for} label="Media battuti" color="#39FF14" testId={`corner-avg-for-${side}`} />
        <BigAvg value={a.corners_against} label="Media concessi" color="#FF3B30" testId={`corner-avg-against-${side}`} />
        <BigAvg value={a.corners_total} label="Media totale" color="#007AFF" testId={`corner-avg-total-${side}`} />
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="border border-[#27272A] px-3 py-2 flex justify-between"><span className="text-[#A1A1AA]">Battuti {venueLabel}</span><b className="mono">{f1(venueFor)}</b></div>
        <div className="border border-[#27272A] px-3 py-2 flex justify-between"><span className="text-[#A1A1AA]">Concessi {venueLabel}</span><b className="mono">{f1(venueAgainst)}</b></div>
        <div className="border border-[#27272A] px-3 py-2 flex justify-between"><span className="text-[#A1A1AA]">Max / Min</span><b className="mono">{stats.corners_max ?? "–"} / {stats.corners_min ?? "–"}</b></div>
        <div className="border border-[#27272A] px-3 py-2 flex justify-between"><span className="text-[#A1A1AA]">Vince sfida corner</span><b className="mono" style={{ color: hotColor(stats.won_corner_battle) }}>{stats.won_corner_battle ?? "–"}%</b></div>
      </div>
      <div className="h-40" data-testid={`corner-chart-${side}`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart} barGap={1} margin={CHART_MARGIN}>
            <XAxis dataKey="n" tick={AXIS_TICK} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip cursor={TOOLTIP_CURSOR} contentStyle={TOOLTIP_STYLE} labelFormatter={(_, p) => p?.[0]?.payload?.label} />
            <ReferenceLine y={a.corners_for} stroke="#39FF14" strokeDasharray="3 3" />
            <RBar dataKey="Battuti" fill="#39FF14" />
            <RBar dataKey="Concessi" fill="#FF3B30" fillOpacity={0.7} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-2.5">
        <div className="label text-[9px]">Frequenze ultime {stats.sample} (partite in cui si è verificato)</div>
        <Bar label="Tot. >8.5" value={o.total_over_8_5} />
        <Bar label="Tot. >9.5" value={o.total_over_9_5} />
        <Bar label="Tot. >10.5" value={o.total_over_10_5} />
        <Bar label="Squadra >3.5" value={o.team_over_3_5} />
        <Bar label="Squadra >4.5" value={o.team_over_4_5} />
        <Bar label="Squadra >5.5" value={o.team_over_5_5} />
      </div>
    </div>
  );
}

function MatchLog({ stats, side }) {
  return (
    <div className="space-y-1" data-testid={`corner-log-${side}`}>
      {stats.matches.map((r) => (
        <div key={r.event_id} className="grid grid-cols-[44px_1fr_auto_auto] gap-2 items-center text-xs border border-[#27272A] px-2 py-1.5">
          <span className="mono text-[#71717A]">{new Date(r.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}</span>
          <span className="truncate">{r.venue === "home" ? "vs" : "@"} {r.opponent}</span>
          <span className="mono text-[#A1A1AA]">{r.goals_for ?? "-"}-{r.goals_against ?? "-"}</span>
          <span className="mono font-black w-14 text-right"><span className="text-[#39FF14]">{r.corners_for ?? "-"}</span>-<span className="text-[#FF3B30]">{r.corners_against ?? "-"}</span></span>
        </div>
      ))}
    </div>
  );
}

export default function CornerStats({ match, aiCorners }) {
  const cs = match.corner_stats;
  if (!cs || (!cs.home.sample && !cs.away.sample)) {
    return (
      <div className="surface-raised p-6" data-testid="corner-stats">
        <div className="label">Statistiche Corner</div>
        <p className="text-sm text-[#A1A1AA] mt-2">Dati corner non disponibili per queste squadre.</p>
      </div>
    );
  }
  const pr = cs.projection;
  return (
    <div className="surface-raised p-6 space-y-8" data-testid="corner-stats">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label flex items-center gap-2"><Flag size={12} strokeWidth={1.5} /> Statistiche Corner · Ultime 10 partite ufficiali</div>
          <p className="text-xs text-[#71717A] mt-1">Fonte: ESPN boxscore. La linea tratteggiata indica la media dei corner battuti.</p>
        </div>
        {pr && (
          <div className="flex gap-2" data-testid="corner-projection">
            <div className="border border-[#27272A] px-3 py-2 text-center"><div className="label text-[9px]">Attesi casa</div><div className="mono font-black text-lg">{f1(pr.expected_home)}</div></div>
            <div className="border border-[#27272A] px-3 py-2 text-center"><div className="label text-[9px]">Attesi ospiti</div><div className="mono font-black text-lg">{f1(pr.expected_away)}</div></div>
            <div className="border border-[#39FF14] bg-[#39FF14]/5 px-3 py-2 text-center"><div className="label text-[9px]">Totale stat.</div><div className="mono font-black text-lg text-[#39FF14]">{f1(pr.expected_total)}</div></div>
            {aiCorners?.expected_corners != null && (
              <div className="border border-[#007AFF] px-3 py-2 text-center"><div className="label text-[9px]">Totale AI</div><div className="mono font-black text-lg text-[#007AFF]">{f1(aiCorners.expected_corners)}</div></div>
            )}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <TeamCorners team={match.home} stats={cs.home} side="home" />
        <TeamCorners team={match.away} stats={cs.away} side="away" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div><div className="label text-[10px] mb-2">Partita per partita · {match.home.short} (corner battuti-concessi)</div><MatchLog stats={cs.home} side="home" /></div>
        <div><div className="label text-[10px] mb-2">Partita per partita · {match.away.short} (corner battuti-concessi)</div><MatchLog stats={cs.away} side="away" /></div>
      </div>
      {cs.h2h.length > 0 && (
        <div data-testid="corner-h2h">
          <div className="label text-[10px] mb-2">Corner negli scontri diretti · media totale <span className="text-[#39FF14]">{f1(cs.h2h_avg_total)}</span></div>
          <div className="flex flex-wrap gap-2">
            {cs.h2h.map((h) => (
              <div key={h.event_id} className="border border-[#27272A] px-3 py-1.5 mono text-sm">
                {match.home.short} <b>{h.home_team_corners ?? "-"}</b> · <b>{h.away_team_corners ?? "-"}</b> {match.away.short}
              </div>
            ))}
          </div>
        </div>
      )}
      <TeamStatsCompare match={match} />
    </div>
  );
}

function TeamStatsCompare({ match }) {
  const h = match.corner_stats.home.averages;
  const a = match.corner_stats.away.averages;
  const rows = [
    ["Gol fatti", "goals_for"], ["Gol subiti", "goals_against"], ["Tiri", "shots"], ["Tiri in porta", "shots_on_target"],
    ["Possesso %", "possession"], ["Cross", "crosses"], ["Falli", "fouls"], ["Gialli", "yellow_cards"],
  ];
  return (
    <div data-testid="team-stats-compare">
      <div className="label text-[10px] mb-3">Medie rendimento · ultime 10</div>
      <div className="space-y-1.5">
        {rows.map(([label, k]) => {
          const hv = h[k] ?? 0, av = a[k] ?? 0, tot = hv + av || 1;
          return (
            <div key={k} className="grid grid-cols-[48px_1fr_110px_1fr_48px] items-center gap-2 text-xs">
              <span className="mono font-bold text-right">{f1(h[k])}</span>
              <div className="h-1.5 bg-[#1A1A1A] flex justify-end"><div className="h-full bg-[#39FF14]" style={{ width: `${(100 * hv) / tot}%` }} /></div>
              <span className="label text-[9px] text-center">{label}</span>
              <div className="h-1.5 bg-[#1A1A1A]"><div className="h-full bg-[#007AFF]" style={{ width: `${(100 * av) / tot}%` }} /></div>
              <span className="mono font-bold">{f1(a[k])}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
